import { PropsWithChildren, createContext, useCallback, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import IcChevronDown from 'src/assets/images/icChevronDownBold.svg';
import useOutsideClick from 'src/hooks/useClickOutside';
const PotentialToggleTriggerBase = styled.div<{
  isClickable: boolean;
}>`
  cursor: ${({
  isClickable
}) => isClickable ? 'pointer' : 'default'};
`;
const Root = styled(PotentialToggleTriggerBase)<{
  isOpen: boolean;
}>`
  --accordion-gap-open: 0px;
  --accordion-gap-close: 0px;

  display: flex;
  flex-direction: column;
  transition: gap 0.3s ease;
  gap: ${({
  isOpen
}) => isOpen ? 'var(--accordion-gap-open)' : 'var(--accordion-gap-close)'};
`;
const Heading = styled(PotentialToggleTriggerBase)``;
const ChevronIcon = styled(IcChevronDown)<{
  isOpen: boolean;
}>`
  transform: ${({
  isOpen
}) => isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.3s ease;
  color: ${({
  theme
}) => theme.color.contentDeep};
  width: 24px;
  height: 24px;
  flex-shrink: 0;

  ${({
  theme
}) => theme.screenSize.mobile} {
    width: 20px;
    height: 20px;
  }
`;
const ExpandedContent = styled.div<{
  isOpen: boolean;
}>`
  display: grid;
  grid-template-rows: ${({
  isOpen
}) => isOpen ? '1fr' : '0fr'};
  transition: grid-template-rows 0.3s ease;
  overflow: hidden;
`;
const ExpandedContentInner = styled.div<{
  isOpen: boolean;
}>`
  min-height: 0;
  opacity: ${({
  isOpen
}) => isOpen ? 1 : 0};
  transition: opacity 0.3s ease;
`;
type ToggleTrigger = 'container' | 'heading';
interface AccordionContextValue {
  isOpen: boolean;
  toggle: () => void;
  toggleTrigger: ToggleTrigger;
}
const AccordionContext = createContext<AccordionContextValue | null>(null);
function useAccordionContext(): AccordionContextValue {
  const ctx = useContext(AccordionContext);
  if (!ctx) {
    throw new Error('Accordion compound components must be used within <Accordion>');
  }
  return ctx;
}
type RootProps = PropsWithChildren<{
  className?: string;

  /**
   * 어디를 클릭하면 토글되는지 정의
   * - 'container': 루트 컨테이너 클릭 시 토글 (기존 동작과 동일)
   * - 'heading': 헤딩 구역 클릭 시에만 토글
   */
  toggleTrigger?: ToggleTrigger;
  closeWhenClickOutside?: boolean;
}>;
const AccordionRoot = ({
  className,
  children,
  toggleTrigger = 'container',
  closeWhenClickOutside = false
}: RootProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const toggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, [setIsOpen]);
  const contextValue = useMemo<AccordionContextValue>(() => ({
    isOpen,
    toggle,
    toggleTrigger
  }), [isOpen, toggle, toggleTrigger]);
  const isToggleTrigger = toggleTrigger === 'container';
  const handleClickOutside = useCallback(() => {
    if (closeWhenClickOutside) {
      setIsOpen(false);
    }
  }, [closeWhenClickOutside]);
  const ref = useOutsideClick<HTMLDivElement>(handleClickOutside);
  return <AccordionContext.Provider value={contextValue} data-sentry-element="AccordionContext.Provider" data-sentry-component="AccordionRoot" data-sentry-source-file="index.tsx">
      <Root ref={ref} className={className} {...isToggleTrigger ? {
      onClick: toggle,
      role: 'button',
      tabIndex: 0
    } : {}} isClickable={isToggleTrigger} isOpen={isOpen} aria-expanded={isOpen} data-sentry-element="Root" data-sentry-source-file="index.tsx">
        {children}
      </Root>
    </AccordionContext.Provider>;
};
type HeadingProps = PropsWithChildren<{
  className?: string;
}>;
const AccordionHeading = ({
  className,
  children
}: HeadingProps) => {
  const {
    toggle,
    toggleTrigger
  } = useAccordionContext();
  const isToggleTrigger = toggleTrigger === 'heading';
  return <Heading className={className} {...isToggleTrigger ? {
    onClick: toggle,
    role: 'button',
    tabIndex: 0
  } : {}} isClickable={isToggleTrigger} data-sentry-element="Heading" data-sentry-component="AccordionHeading" data-sentry-source-file="index.tsx">
      {children}
    </Heading>;
};
type IconProps = {
  className?: string;
};
const AccordionIcon = ({
  className
}: IconProps) => {
  const {
    isOpen
  } = useAccordionContext();
  return <ChevronIcon className={className} isOpen={isOpen} data-sentry-element="ChevronIcon" data-sentry-component="AccordionIcon" data-sentry-source-file="index.tsx" />;
};
type PanelProps = PropsWithChildren<{
  className?: string;
}>;
const AccordionPanel = ({
  className,
  children
}: PanelProps) => {
  const {
    isOpen
  } = useAccordionContext();
  return <ExpandedContent className={className} isOpen={isOpen} data-sentry-element="ExpandedContent" data-sentry-component="AccordionPanel" data-sentry-source-file="index.tsx">
      <ExpandedContentInner isOpen={isOpen} data-sentry-element="ExpandedContentInner" data-sentry-source-file="index.tsx">{children}</ExpandedContentInner>
    </ExpandedContent>;
};
const Accordion = Object.assign(AccordionRoot, {
  Heading: AccordionHeading,
  Panel: AccordionPanel,
  Icon: AccordionIcon
});
export default Accordion;